// ============================================================================
// rovoPack-renderer.js
//
// Renders the Rovo Agent Pack JSON into a self-contained HTML document.
// Card style matches the existing automation pack renderers but uses
// Rovo product colour coding throughout.
// ============================================================================

const esc = (v = '') =>
  String(v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// ── Rovo product colours ──────────────────────────────────────────────────────
const ROVO = {
  'Rovo Agents': { bg: '#E6F1FB', text: '#0052CC', border: '#85B7EB', bar: '#0052CC' },
  'Rovo Search': { bg: '#EAE6FF', text: '#5243AA', border: '#998DD9', bar: '#6554C0' },
  'Rovo Chat':   { bg: '#E3FCEF', text: '#006644', border: '#57D9A3', bar: '#00875A' },
  'Rovo Studio': { bg: '#FFF0B3', text: '#974F0C', border: '#FFE380', bar: '#FF8B00' },
};
const getRovo = (p) => ROVO[p] || { bg: '#F4F5F7', text: '#42526E', border: '#DFE1E6', bar: '#97A0AF' };

const priorityColors = {
  Critical: { bg: '#FFEBE6', text: '#BF2600', border: '#FF8F73' },
  High:     { bg: '#FFF0B3', text: '#974F0C', border: '#FFE380' },
  Medium:   { bg: '#DEEBFF', text: '#0747A6', border: '#B3D4FF' },
};
const effortColors = {
  Low:    { bg: '#E3FCEF', text: '#006644', border: '#57D9A3' },
  Medium: { bg: '#FFF0B3', text: '#974F0C', border: '#FFE380' },
  High:   { bg: '#FFEBE6', text: '#BF2600', border: '#FF8F73' },
};
const readinessColors = {
  High:   { bg: '#E3FCEF', text: '#006644', border: '#57D9A3' },
  Medium: { bg: '#FFF0B3', text: '#974F0C', border: '#FFE380' },
  Low:    { bg: '#FFEBE6', text: '#BF2600', border: '#FF8F73' },
};

const pill = (text, { bg, text: tc, border }) =>
  `<span style="display:inline-flex;align-items:center;padding:2px 9px;border-radius:999px;font-size:11px;font-weight:700;background:${bg};color:${tc};border:1px solid ${border};">${esc(text)}</span>`;

const CSS = `
  html,body{margin:0;padding:0;}*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','Helvetica Neue',Arial,sans-serif;background:#F4F5F7;color:#172B4D;}
  .wrap{max-width:1100px;margin:0 auto;padding:16px;}
  .doc-header{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:14px 20px;margin-bottom:12px;display:flex;justify-content:space-between;align-items:center;}
  .doc-header-left{display:flex;flex-direction:column;gap:4px;}
  .doc-header-title{font-size:16px;font-weight:700;color:#172B4D;}
  .doc-header-type{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:#0052CC;}
  .doc-header-time{font-size:12px;color:#6B778C;}
  .hero{background:#fff;border:1px solid #DFE1E6;border-radius:10px;overflow:hidden;margin-bottom:12px;}
  .hero-bar{height:4px;background:linear-gradient(90deg,#0052CC,#6554C0,#00875A,#FF8B00);}
  .hero-body{padding:20px 24px;}
  .hero-title{font-size:26px;font-weight:800;color:#172B4D;margin-bottom:6px;}
  .hero-sub{font-size:13px;color:#42526E;line-height:1.6;margin-bottom:14px;}
  .hero-meta{display:flex;flex-wrap:wrap;gap:8px;}
  .legend{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px;padding:10px 14px;background:#fff;border:1px solid #DFE1E6;border-radius:10px;}
  .legend-label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-right:4px;}
  .agent{background:#fff;border:1px solid #DFE1E6;border-radius:10px;margin-bottom:10px;overflow:hidden;}
  .agent-top{padding:14px 16px;border-bottom:1px solid #EBECF0;}
  .agent-num{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#6B778C;margin-bottom:5px;}
  .agent-name{font-size:16px;font-weight:700;color:#172B4D;margin-bottom:8px;}
  .agent-badges{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:6px;}
  .rovo-tag{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:6px;font-size:12px;font-weight:700;}
  .rovo-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;}
  .studio-badge{display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:999px;font-size:10px;font-weight:700;background:#FFF0B3;color:#974F0C;border:1px solid #FFE380;}
  .agent-body{display:grid;grid-template-columns:1fr 1fr;}
  .cell{padding:12px 16px;border-right:1px solid #EBECF0;border-bottom:1px solid #EBECF0;}
  .cell:nth-child(2n){border-right:none;}
  .cell:nth-last-child(-n+2){border-bottom:none;}
  .cell-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:5px;}
  .cell-text{font-size:13px;line-height:1.65;color:#42526E;}
  .cell-text ul{margin:4px 0 0 0;padding-left:16px;}
  .cell-text li{margin-bottom:3px;}
  .pairing-row{padding:10px 16px;background:#F0F7FF;border-top:1px solid #EBECF0;font-size:12px;color:#0747A6;line-height:1.6;}
  .pairing-row strong{font-weight:700;}
  .prereq-row{padding:10px 16px;background:#FAFBFC;border-top:1px solid #EBECF0;}
  .prereq-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:5px;}
  .prereq-item{display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:999px;background:#EBECF0;color:#42526E;font-size:11px;font-weight:600;margin:2px 3px 2px 0;}
  .phase-list{display:flex;flex-direction:column;gap:8px;margin-bottom:12px;}
  .phase{border:1px solid #EBECF0;border-radius:8px;background:#FAFBFC;overflow:hidden;}
  .phase-top{display:flex;align-items:flex-start;gap:12px;padding:12px 14px;}
  .phase-num{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:800;color:#fff;flex-shrink:0;margin-top:1px;}
  .phase-name{font-size:13px;font-weight:700;color:#172B4D;}
  .phase-desc{font-size:12px;color:#42526E;margin-top:3px;line-height:1.55;}
  .discovery_rules{background:#fff;border:1px solid #DFE1E6;border-radius:10px;padding:16px 18px;margin-bottom:12px;}
  .discovery_rules-title{font-size:13px;font-weight:700;color:#172B4D;margin-bottom:10px;}
  .discovery_rule-item{padding:7px 0;border-bottom:1px solid #EBECF0;font-size:13px;color:#42526E;line-height:1.6;padding-left:12px;border-left:3px solid #0052CC;margin-bottom:6px;}
  .discovery_rule-item:last-child{border-bottom:none;margin-bottom:0;}
  @media(max-width:700px){.agent-body{grid-template-columns:1fr;}.cell{border-right:none;}}
  @media print{body{background:#fff;}.agent,.hero{break-inside:avoid;}}
  .impl-block{margin:0;border-top:1px solid #EBECF0;}
  .impl-header{display:flex;align-items:center;justify-content:space-between;padding:8px 16px;background:#1e2432;}
  .impl-header-left{display:flex;align-items:center;gap:8px;}
  .impl-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#8892b0;}
  .impl-lang{font-size:10px;font-weight:700;padding:2px 7px;border-radius:3px;background:#0052CC22;color:#4C8FEB;}
  .impl-copy{background:#0052CC;color:#fff;border:none;padding:3px 10px;border-radius:4px;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit;}
  .impl-copy:hover{background:#0747A6;}
  .impl-code{background:#1e2432;padding:14px 16px;font-family:Monaco,Menlo,'Ubuntu Mono',monospace;font-size:12px;color:#e8eeff;line-height:1.65;overflow-x:auto;white-space:pre-wrap;word-break:break-word;}
`;

// ── Document header ───────────────────────────────────────────────────────────
const renderDocHeader = (modelName) => {
  const timestamp = new Date().toLocaleString('en-US', { 
    year: 'numeric', month: 'short', day: 'numeric', 
    hour: '2-digit', minute: '2-digit', hour12: true 
  });
  return `
<div class="doc-header">
  <div class="doc-header-left">
    <div class="doc-header-type">Rovo Pack</div>
    <div class="doc-header-title">${esc(modelName || 'Process Model')}</div>
  </div>
  <div class="doc-header-time">${timestamp}</div>
</div>`;
};

// ── Build agent cards ─────────────────────────────────────────────────────────
const buildAgents = (agents = []) => agents.map((a, i) => {
  const rc = getRovo(a.rovo_product);
  const pc = priorityColors[a.priority] || priorityColors.Medium;
  const ec = effortColors[a.setup_effort] || effortColors.Medium;

  const actions = (a.actions || []).map(x => `<li>${esc(x)}</li>`).join('');
  const prereqs = (a.prerequisites || []).map(x =>
    `<span class="prereq-item">✓ ${esc(x)}</span>`).join('');
  const studioBlock = a.requires_studio && a.rovo_studio_config
    ? `<div class="cell"><div class="cell-label">Rovo Studio config</div><div class="cell-text">${esc(a.rovo_studio_config)}</div></div>`
    : '';

  return `
<div class="agent">
  <div class="agent-top">
    <div class="agent-num">Agent ${i + 1}</div>
    <div class="agent-name">${esc(a.name)}</div>
    <div class="agent-badges">
      ${pill(a.priority, pc)}
      ${pill(`Setup: ${a.setup_effort}`, ec)}
    </div>
    <div style="display:flex;align-items:center;gap:8px;margin-top:6px;">
      <div class="rovo-tag" style="background:${rc.bg};color:${rc.text};border:1px solid ${rc.border};">
        <span class="rovo-dot" style="background:${rc.bar};"></span>
        ${esc(a.rovo_product)}
      </div>
      ${a.requires_studio ? '<span class="studio-badge">★ Rovo Studio</span>' : ''}
    </div>
  </div>
  <div class="agent-body">
    <div class="cell">
      <div class="cell-label">Evidence signal</div>
      <div class="cell-text">${esc(a.evidence_signal)}</div>
    </div>
    <div class="cell">
      <div class="cell-label">Trigger</div>
      <div class="cell-text">${esc(a.trigger)}</div>
    </div>
    <div class="cell">
      <div class="cell-label">Actions</div>
      <div class="cell-text"><ul>${actions}</ul></div>
    </div>
    <div class="cell">
      <div class="cell-label">Expected benefit</div>
      <div class="cell-text">${esc(a.expected_benefit)}</div>
    </div>
    ${studioBlock}
  </div>
  <div class="pairing-row"><strong>Jira Automation pairing:</strong> ${esc(a.jira_automation_pairing)}</div>
  ${prereqs ? `<div class="prereq-row"><div class="prereq-label">Prerequisites</div>${prereqs}</div>` : ''}
  ${a.agent_instructions ? `
  <div class="impl-block">
    <div class="impl-header">
      <div class="impl-header-left">
        <span class="impl-label">Agent Instructions</span>
        <span class="impl-lang">Paste into Rovo Agent</span>
      </div>
      <button class="impl-copy" onclick="navigator.clipboard.writeText(this.closest('.impl-block').querySelector('pre').textContent)">Copy</button>
    </div>
    <pre class="impl-code">${esc(a.agent_instructions)}</pre>
  </div>` : ''}
  ${a.jira_automation_json ? `
  <div class="impl-block">
    <div class="impl-header">
      <div class="impl-header-left">
        <span class="impl-label">Jira Automation Rule</span>
        <span class="impl-lang">Importable JSON</span>
      </div>
      <button class="impl-copy" onclick="navigator.clipboard.writeText(this.closest('.impl-block').querySelector('pre').textContent)">Copy</button>
    </div>
    <pre class="impl-code">${esc(JSON.stringify(a.jira_automation_json, null, 2))}</pre>
  </div>` : ''}
</div>`;
}).join('');

// ── Build implementation sequence ─────────────────────────────────────────────
const buildSequence = (seq = {}) => {
  const phases = [
    { key: 'phase_1_no_build', color: '#00875A', num: 1 },
    { key: 'phase_2_agents',   color: '#0052CC', num: 2 },
    { key: 'phase_3_studio',   color: '#FF8B00', num: 3 },
  ];
  return `
<div class="discovery_rules" style="margin-bottom:12px;">
  <div class="discoveryRules-title">Implementation Sequence</div>
  <div class="phase-list">
    ${phases.map(p => {
      const ph = seq[p.key];
      if (!ph) return '';
      return `
    <div class="phase">
      <div class="phase-top">
        <div class="phase-num" style="background:${p.color};">${p.num}</div>
        <div>
          <div class="phase-name">${esc(ph.label)}</div>
          <div class="phase-desc">${esc(ph.description)}</div>
        </div>
      </div>
    </div>`;
    }).join('')}
  </div>
</div>`;
};

// ── Rovo product legend ───────────────────────────────────────────────────────
const buildLegend = () => {
  const items = Object.entries(ROVO).map(([name, c]) =>
    `<span class="rovo-tag" style="background:${c.bg};color:${c.text};border:1px solid ${c.border};">
      <span class="rovo-dot" style="background:${c.bar};"></span>${esc(name)}
    </span>`).join('');
  return `<div class="legend"><span class="legend-label">Rovo products</span>${items}</div>`;
};

// ── Main export ───────────────────────────────────────────────────────────────
export const renderRovoPackHTML = (data, modelTitle = '') => {
  if (!data) return '<p style="padding:20px;color:#6B778C;">No data available.</p>';

  const rc = readinessColors[data.rovo_readiness] || readinessColors.Medium;
  const discoveryRules = (data.discovery_rules_summary || []).map(f =>
    `<div class="discoveryRule-item">${esc(f)}</div>`).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Rovo Agent Pack${modelTitle ? ` — ${esc(modelTitle)}` : ''}</title>
<style>${CSS}</style>
</head>
<body>
<div class="wrap">

  <div class="hero">
    <div class="hero-bar"></div>
    <div class="hero-body">
      <div class="hero-title">${esc(data.pack_title || 'Rovo Agent Pack')}</div>
      <div class="hero-sub">${esc(data.workflow_context || '')} ${esc(modelTitle)}</div>
      <div class="hero-meta">
        ${pill(`Rovo Readiness: ${data.rovo_readiness || 'Medium'}`, rc)}
        <span style="font-size:12px;color:#42526E;align-self:center;">${esc(data.rovo_readiness_rationale || '')}</span>
      </div>
    </div>
  </div>

  ${discoveryRules ? `<div class="discovery_rules"><div class="discoveryRules-title">Key Discovery Rules</div>${discoveryRules}</div>` : ''}

  ${buildLegend()}

  ${buildSequence(data.implementation_sequence)}

  <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#6B778C;margin-bottom:8px;">
    Rovo Agents (${(data.agents || []).length})
  </div>
  ${buildAgents(data.agents)}

</div>
<script>
// Report content height to parent so iframe can resize without double scrollbars
const reportHeight = () => {
  const h = document.documentElement.scrollHeight || document.body.scrollHeight;
  window.parent.postMessage({ type: 'rovo-height', height: h }, '*');
};
window.addEventListener('load', reportHeight);
new ResizeObserver(reportHeight).observe(document.body);

document.querySelectorAll('.impl-copy').forEach(btn => {
  btn.addEventListener('click', function() {
    const pre = this.closest('.impl-block').querySelector('pre');
    const text = pre.textContent;
    const btn = this;
    const flash = () => {
      btn.textContent = 'Copied!';
      btn.style.background = '#00875A';
      setTimeout(() => { btn.textContent = 'Copy'; btn.style.background = ''; }, 1500);
    };
    // Try execCommand first (works in sandboxed iframes)
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) { flash(); return; }
    } catch(e) {}
    // Fallback: postMessage to parent frame
    try {
      window.parent.postMessage({ type: 'rovo-copy', text }, '*');
      flash();
    } catch(e) {}
  });
});
</script>
</body>
</html>`;
};

export const buildRovoPackExportHTML = (data, modelTitle = '') => {
  if (!data) return '<p style="padding:20px;color:#6B778C;">No data available.</p>';

  const rc = readinessColors[data.rovo_readiness] || readinessColors.Medium;
  const discoveryRules = (data.discovery_rules_summary || []).map(f =>
    `<div class="discoveryRule-item">${esc(f)}</div>`).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Rovo Agent Pack${modelTitle ? ` — ${esc(modelTitle)}` : ''}</title>
<style>${CSS}</style>
</head>
<body>
<div class="wrap">

  ${renderDocHeader(modelTitle)}

  <div class="hero">
    <div class="hero-bar"></div>
    <div class="hero-body">
      <div class="hero-title">${esc(data.pack_title || 'Rovo Agent Pack')}</div>
      <div class="hero-sub">${esc(data.workflow_context || '')} ${esc(modelTitle)}</div>
      <div class="hero-meta">
        ${pill(`Rovo Readiness: ${data.rovo_readiness || 'Medium'}`, rc)}
        <span style="font-size:12px;color:#42526E;align-self:center;">${esc(data.rovo_readiness_rationale || '')}</span>
      </div>
    </div>
  </div>

  ${discoveryRules ? `<div class="discovery_rules"><div class="discoveryRules-title">Key Discovery Rules</div>${discoveryRules}</div>` : ''}

  ${buildLegend()}

  ${buildSequence(data.implementation_sequence)}

  <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#6B778C;margin-bottom:8px;">
    Rovo Agents (${(data.agents || []).length})
  </div>
  ${buildAgents(data.agents)}

</div>
<script>
const reportHeight = () => {
  const h = document.documentElement.scrollHeight || document.body.scrollHeight;
  parent.postMessage({type:'reportHeight',height:h},'*');
};
reportHeight();
window.addEventListener('load',reportHeight);
new ResizeObserver(reportHeight).observe(document.body);
</script>
</body>
</html>`;
};
